import React, { useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import SunEmblem from './SunEmblem';
import { brand } from '../mock/mock';

const HANUMAN_IMG =
  'https://images.unsplash.com/photo-1686582557983-0df22fd25187?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxIYW51bWFufGVufDB8fHx8MTc4ODAxOTM1M3ww&ixlib=rb-4.1.0&q=85';

const FLOAT_NUMBERS = [
  { n: '1', top: '8%', left: '82%', d: '0s' },
  { n: '2', top: '20%', left: '90%', d: '1.2s' },
  { n: '3', top: '42%', left: '86%', d: '2s' },
  { n: '4', top: '58%', left: '78%', d: '0.6s' },
  { n: '8', top: '18%', left: '10%', d: '1.6s' },
  { n: '7', top: '40%', left: '6%', d: '0.4s' },
  { n: '6', top: '58%', left: '12%', d: '2.4s' },
  { n: '5', top: '30%', left: '18%', d: '1s' },
];

export default function Hero() {
  const hanumanRef = useRef(null);
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  // Sway Hanuman ji left and right in the sky as the user scrolls.
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const y = window.scrollY;
          if (hanumanRef.current) {
            const sway = Math.sin(y / 260) * 90; // horizontal drift
            const rise = y * 0.18; // gentle parallax upward
            hanumanRef.current.style.transform = `translate3d(${sway}px, ${-rise}px, 0)`;
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* White-gold heaven background */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(circle at 50% 22%, #ffffff 0%, #fff8e6 34%, #fbeecb 60%, #f6e3b8 100%)',
        }}
      />
      {/* Heavenly light rays */}
      <div
        className="absolute inset-0 opacity-70"
        style={{
          background:
            'conic-gradient(from 180deg at 50% 20%, rgba(255,255,255,0) 0deg, rgba(255,240,200,0.5) 12deg, rgba(255,255,255,0) 24deg, rgba(255,240,200,0.4) 40deg, rgba(255,255,255,0) 56deg, rgba(255,240,200,0.5) 74deg, rgba(255,255,255,0) 90deg)',
          maskImage: 'radial-gradient(circle at 50% 20%, #000 0%, transparent 70%)',
          WebkitMaskImage: 'radial-gradient(circle at 50% 20%, #000 0%, transparent 70%)',
        }}
      />

      {/* Hanuman ji drifting in the sky */}
      <div
        ref={hanumanRef}
        className="absolute left-1/2 top-[6%] -translate-x-1/2 w-[520px] max-w-[85vw] pointer-events-none will-change-transform"
        style={{ transition: 'transform 0.15s linear' }}
      >
        <img
          src={HANUMAN_IMG}
          alt="Lord Hanuman"
          className="w-full h-auto select-none"
          style={{
            opacity: 0.22,
            mixBlendMode: 'multiply',
            maskImage:
              'radial-gradient(ellipse 60% 65% at 50% 45%, #000 40%, transparent 78%)',
            WebkitMaskImage:
              'radial-gradient(ellipse 60% 65% at 50% 45%, #000 40%, transparent 78%)',
          }}
          draggable={false}
        />
      </div>

      {/* Soft base fade into page */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(180deg, rgba(251,243,226,0) 55%, #fbf3e2 100%)',
        }}
      />
      {/* Golden center glow */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(circle at 50% 34%, rgba(255,240,190,0.55) 0%, rgba(255,240,190,0) 52%)',
        }}
      />

      {/* Floating numbers */}
      {FLOAT_NUMBERS.map((f, i) => (
        <span
          key={i}
          className="absolute font-display text-[#c9962e]/35 select-none animate-float hidden md:block"
          style={{
            top: f.top,
            left: f.left,
            fontSize: '2.6rem',
            animationDelay: f.d,
          }}
        >
          {f.n}
        </span>
      ))}

      {/* Content */}
      <div className="relative z-10 w-full max-w-4xl mx-auto px-5 text-center pt-28 pb-16">
        <div className="flex justify-center mb-2">
          <div className="reveal reveal-scale">
            <SunEmblem size={280} />
          </div>
        </div>

        <h1 className="reveal relative md:-left-10 font-display font-bold leading-none" data-delay="120">
          <span className="inline-block text-maroon-gradient text-[2.5rem] sm:text-6xl md:text-8xl drop-shadow-[0_3px_2px_rgba(90,13,10,0.25)]">
            NUMEROLOGY
          </span>
          <span className="inline-block text-gold-gradient text-[2.5rem] sm:text-6xl md:text-8xl ml-3 drop-shadow-[0_3px_2px_rgba(164,118,27,0.25)]">
            KING
          </span>
        </h1>

        <p className="reveal font-heading font-bold tracking-[0.25em] sm:tracking-[0.4em] text-[#a4761b] text-xs sm:text-sm md:text-base mt-4" data-delay="220">
          {brand.by.toUpperCase()}
        </p>

        {/* Ribbon tagline */}
        <div className="reveal flex justify-center mt-6" data-delay="320">
          <div
            className="px-4 sm:px-8 py-2.5 rounded-md max-w-full"
            style={{
              background: 'linear-gradient(90deg, #7c130f, #a71c14, #7c130f)',
              border: '1.5px solid #d9a63b',
              boxShadow: '0 6px 22px rgba(124,19,15,0.35)',
            }}
          >
            <span className="font-heading tracking-[0.12em] sm:tracking-[0.25em] text-[#f2d68a] text-xs sm:text-sm md:text-base">
              {brand.tagline}
            </span>
          </div>
        </div>

        <p className="reveal font-body italic text-[#5a3a1c] text-lg sm:text-xl md:text-2xl mt-7" data-delay="420">
          {brand.subtitle}
        </p>
        <p className="reveal font-body italic text-[#7c4a20] text-base sm:text-lg md:text-xl max-w-2xl mx-auto mt-1" data-delay="480">
          {brand.intro}
        </p>

        <div className="reveal flex flex-wrap gap-4 justify-center mt-9" data-delay="560">
          <button
            onClick={() => scrollTo('booking')}
            className="px-8 py-3 rounded-full font-heading tracking-wide text-[#fff8e7] transition-all duration-300 hover:scale-105"
            style={{
              background: 'linear-gradient(90deg, #a71c14, #7c130f)',
              border: '1.5px solid #d9a63b',
              boxShadow: '0 8px 26px rgba(124,19,15,0.4)',
            }}
          >
            Book Your Appointment
          </button>
          <button
            onClick={() => scrollTo('services')}
            className="px-8 py-3 rounded-full font-heading tracking-wide text-[#7c130f] bg-[#fbf3e2]/70 backdrop-blur-sm transition-all duration-300 hover:bg-[#f4e6c8]"
            style={{ border: '1.5px solid #c9962e' }}
          >
            Explore Services
          </button>
        </div>
      </div>

      {/* Scroll cue */}
      <button
        onClick={() => scrollTo('about')}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-[#a4761b] animate-bounce"
        aria-label="Scroll down"
      >
        <ChevronDown size={30} />
      </button>
    </section>
  );
}
